import { useCallback, useRef, useState } from "react";
import { Button, ErrorState, Spinner } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { useOptionApi } from "@/hooks/apiHooks/useOptionApi";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import { useMountRead } from "@/hooks/useMountRead";
import type { OptionResponse } from "@models/option";
import { AddOptionRow } from "./_components/AddOptionRow";
import { OptionGroupRail } from "./_components/OptionGroupRail";
import { OptionValuePills } from "./_components/OptionValuePills";
import {
    offeredOptions,
    picksOnOffer,
    togglePick,
    type VariantSelection,
} from "./_helpers/variantSelection";

export type { VariantSelection };

interface VariantOptionsModalProps {
    /** The picks to open with, so a re-opened picker shows what was picked. */
    initialSelection: VariantSelection;
    /** Close, Escape and a backdrop click all hand back the picks. */
    onClose: (selection: VariantSelection) => void;
}

// The "+ Variant Option" picker: one value in any number of option groups,
// and "Add New Option" to create a group with its values. Mounted only while
// open. There is no Cancel, so every way out hands back the picks.
// Not opened by ProductForm yet: what Save persists awaits a PO ruling.
export const VariantOptionsModal = ({
    initialSelection,
    onClose,
}: VariantOptionsModalProps) => {
    const { getOptions, createOption } = useOptionApi();
    // What this picker's own read offers; null until a read has landed.
    const [options, setOptions] = useState<OptionResponse[] | null>(null);
    const [selection, setSelection] = useState(initialSelection);
    // The group the admin opened. Falls back to the first group at render
    // time when null or no longer offered.
    const [requestedOptionId, setRequestedOptionId] = useState<number | null>(
        null,
    );
    const [isAdding, setIsAdding] = useState(false);
    const [addFailed, setAddFailed] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);

    // The failed state below is the message, so the read's toast is skipped
    // (one failure, one alert, #223).
    const read = useCallback(
        (isCurrent: () => boolean) =>
            getOptions({ skipErrorNotification: true }).then((data) => {
                if (isCurrent()) setOptions(offeredOptions(data));
            }),
        [getOptions],
    );
    const { readStatus, reload } = useMountRead(read);

    // Until a list has loaded nothing can be judged, so the picks stand as
    // given.
    const picks = options ? picksOnOffer(selection, options) : selection;
    const openOption =
        options?.find((option) => option.id === requestedOptionId) ??
        options?.[0];

    const handleClose = () => onClose(picks);

    // Escape and the backdrop are refused while a create is in flight (and
    // Close is disabled), so its failure has an open dialog to land on.
    const { backdropProps, panelProps } = useDialogFocusTrap({
        panelRef,
        isProcessing: isAdding,
        onRequestClose: handleClose,
    });

    const handleAdd = async (
        name: string,
        values: string[],
    ): Promise<boolean> => {
        setIsAdding(true);
        setAddFailed(false);
        try {
            const { id } = await createOption({ name, values });
            setRequestedOptionId(id);
            // The new group's values, and their ids, come from a re-read.
            void reload();
            return true;
        } catch {
            setAddFailed(true);
            return false;
        } finally {
            setIsAdding(false);
        }
    };

    // A re-read in flight or failed does NOT leave the held list on screen,
    // although #407 says data in hand normally stays. This is that rule's
    // exception, "unless that data may answer something else": the re-read
    // after Add exists to fetch the new group, and the held list lacks it, so
    // shown alone it would look complete when it is not. The picks are kept
    // in `selection` meanwhile and show again once the list lands.
    let body;
    if (readStatus === "failed") {
        body = (
            <ErrorState
                message="Couldn't load options."
                onRetry={() => void reload()}
            />
        );
    } else if (readStatus === "loading" || !options) {
        body = (
            <div role="status" aria-label="Loading options" className="p-8">
                <Spinner size="lg" />
            </div>
        );
    } else if (!openOption) {
        body = <p className="p-4 text-neutral">No options yet.</p>;
    } else {
        body = (
            <div className="grid min-h-72 grid-cols-[13rem_1fr]">
                <OptionGroupRail
                    options={options}
                    openOptionId={openOption.id}
                    selection={picks}
                    onOpen={setRequestedOptionId}
                />
                <div className="overflow-y-auto rounded border border-base-300 bg-base-100 p-3">
                    <OptionValuePills
                        option={openOption}
                        pickedValueId={picks[openOption.id]}
                        onPick={(valueId) =>
                            setSelection(
                                togglePick(picks, openOption.id, valueId),
                            )
                        }
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <div
                data-testid="variant-options-backdrop"
                {...backdropProps}
                className="fixed inset-0 z-40 bg-black/30"
            />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label="Variant Options"
                {...panelProps}
                className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-[44rem] max-w-[90vw] -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-lg bg-base-100 p-6 text-neutral shadow-xl focus:outline-none"
            >
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-2xl font-bold">Variant Options</h2>
                    <Button
                        outline
                        color={ComponentColor.Primary}
                        size={ComponentSize.SM}
                        disabled={isAdding}
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </div>

                <div className="rounded-lg bg-base-200 p-4">{body}</div>

                {readStatus !== "failed" && options && (
                    <AddOptionRow
                        existingNames={options.map((option) => option.name)}
                        isAdding={isAdding}
                        addFailed={addFailed}
                        onAdd={handleAdd}
                    />
                )}
            </div>
        </>
    );
};
